export type ProductionSceneryCategory = 'nature' | 'landscaping' | 'park' | 'structures';

export type ProductionSceneryAssetId =
  | 'tree'
  | 'rock'
  | 'broadleaf_tree'
  | 'conifer'
  | 'shrub_cluster'
  | 'hedge'
  | 'rock_cluster'
  | 'flower_bed'
  | 'planter'
  | 'path_straight'
  | 'path_corner'
  | 'plaza'
  | 'bench'
  | 'picnic_table'
  | 'bin'
  | 'lamp'
  | 'directional_sign'
  | 'fence_straight'
  | 'fence_corner'
  | 'queue_rail'
  | 'canopy'
  | 'kiosk';

export interface ProductionSceneryDefinition {
  readonly id: ProductionSceneryAssetId;
  readonly label: string;
  readonly category: ProductionSceneryCategory;
  readonly glyph: string;
  readonly footprint: readonly [width: number, depth: number];
  readonly modularRotation: boolean;
  readonly legacyIds?: readonly string[];
}

export const PRODUCTION_SCENERY_KIT_VERSION = '9.4.0';

export const PRODUCTION_SCENERY: readonly ProductionSceneryDefinition[] = Object.freeze([
  {
    id: 'broadleaf_tree',
    label: 'Broadleaf Tree',
    category: 'nature',
    glyph: '♣',
    footprint: [3.5, 3.5],
    modularRotation: false,
    legacyIds: ['tree'],
  },
  {
    id: 'conifer',
    label: 'Conifer',
    category: 'nature',
    glyph: '▲',
    footprint: [2.5, 2.5],
    modularRotation: false,
  },
  {
    id: 'shrub_cluster',
    label: 'Shrub Cluster',
    category: 'nature',
    glyph: '✤',
    footprint: [2.8, 2],
    modularRotation: false,
  },
  {
    id: 'hedge',
    label: 'Hedge',
    category: 'nature',
    glyph: '▰',
    footprint: [3.5, 1.4],
    modularRotation: true,
    legacyIds: ['hedge'],
  },
  {
    id: 'rock_cluster',
    label: 'Rock Cluster',
    category: 'nature',
    glyph: '◆',
    footprint: [2.5, 2],
    modularRotation: false,
    legacyIds: ['rock'],
  },
  {
    id: 'flower_bed',
    label: 'Flower Bed',
    category: 'landscaping',
    glyph: '✿',
    footprint: [3, 1.5],
    modularRotation: true,
  },
  {
    id: 'planter',
    label: 'Planter',
    category: 'landscaping',
    glyph: '▣',
    footprint: [1.8, 1.8],
    modularRotation: true,
  },
  {
    id: 'path_straight',
    label: 'Straight Path',
    category: 'landscaping',
    glyph: '▬',
    footprint: [4, 2],
    modularRotation: true,
  },
  {
    id: 'path_corner',
    label: 'Corner Path',
    category: 'landscaping',
    glyph: '⌞',
    footprint: [4, 4],
    modularRotation: true,
  },
  {
    id: 'plaza',
    label: 'Plaza',
    category: 'landscaping',
    glyph: '▦',
    footprint: [4, 4],
    modularRotation: true,
  },
  {
    id: 'bench',
    label: 'Bench',
    category: 'park',
    glyph: '▥',
    footprint: [2.4, 0.9],
    modularRotation: true,
  },
  {
    id: 'picnic_table',
    label: 'Picnic Table',
    category: 'park',
    glyph: '▤',
    footprint: [2.6, 2.2],
    modularRotation: true,
  },
  {
    id: 'bin',
    label: 'Bin',
    category: 'park',
    glyph: '▧',
    footprint: [0.8, 0.8],
    modularRotation: true,
  },
  {
    id: 'lamp',
    label: 'Park Lamp',
    category: 'park',
    glyph: '●',
    footprint: [0.8, 0.8],
    modularRotation: false,
    legacyIds: ['lamp'],
  },
  {
    id: 'directional_sign',
    label: 'Direction Sign',
    category: 'park',
    glyph: '➜',
    footprint: [1.2, 0.8],
    modularRotation: true,
  },
  {
    id: 'fence_straight',
    label: 'Straight Fence',
    category: 'structures',
    glyph: '╫',
    footprint: [4, 0.5],
    modularRotation: true,
  },
  {
    id: 'fence_corner',
    label: 'Corner Fence',
    category: 'structures',
    glyph: '╚',
    footprint: [4, 4],
    modularRotation: true,
  },
  {
    id: 'queue_rail',
    label: 'Queue Rail',
    category: 'structures',
    glyph: '∥',
    footprint: [3, 1.2],
    modularRotation: true,
  },
  {
    id: 'canopy',
    label: 'Canopy',
    category: 'structures',
    glyph: '⌂',
    footprint: [4, 3],
    modularRotation: true,
  },
  {
    id: 'kiosk',
    label: 'Kiosk',
    category: 'structures',
    glyph: '▩',
    footprint: [3.5, 3],
    modularRotation: true,
  },
]);

const BY_ID = new Map(PRODUCTION_SCENERY.map((definition) => [definition.id, definition]));
const LEGACY = new Map(
  PRODUCTION_SCENERY.flatMap((definition) =>
    (definition.legacyIds ?? []).map((legacyId) => [legacyId, definition.id] as const),
  ),
);

export function normalizeSceneryAssetId(value: string): ProductionSceneryAssetId {
  const normalized = LEGACY.get(value) ?? value;
  return BY_ID.has(normalized as ProductionSceneryAssetId)
    ? (normalized as ProductionSceneryAssetId)
    : 'rock_cluster';
}

export function getProductionSceneryDefinition(value: string): ProductionSceneryDefinition {
  return BY_ID.get(normalizeSceneryAssetId(value))!;
}
